import { formatPesoAmount } from '../utils/money';
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getBranchExpenses,
    createBranchExpense,
    updateBranchExpense,
    deleteBranchExpense,
} from "../api";
import { FiTrash2, FiEdit, FiPlus } from "react-icons/fi";
import ExpenseModal from "../components/ExpenseModal";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import DateRangePresetPicker from "../components/DateRangePresetPicker";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "../components/InlineLoading";
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';

// Helper functions
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).toISOString().split("T")[0];
const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).toISOString().split("T")[0];

const BranchExpensesPage = () => {
    const branchId = useParams().branchId || localStorage.getItem("selectedBranchId");

    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth()); // 0-based index
    const [customFilter, setCustomFilter] = useState(false);
    const [startDate, setStartDate] = useState(getFirstDayOfMonth(now.getFullYear(), now.getMonth()));
    const [endDate, setEndDate] = useState(getLastDayOfMonth(now.getFullYear(), now.getMonth()));

    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const { confirm } = useConfirmDialog();
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalExpenses, setTotalExpenses] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const { user } = useCurrentUser();
    const pageSize = 12;

    const canCreateExpense = hasPermission(user, "branch_expense.create");
    const canUpdateExpense = hasPermission(user, "branch_expense.update");
    const canDeleteExpense = hasPermission(user, "branch_expense.delete");

    // Adjust start/end dates when month or year changes (only if not using custom filter)
    useEffect(() => {
        if (!customFilter) {
            const newStart = getFirstDayOfMonth(year, month);
            const newEnd = getLastDayOfMonth(year, month);
            setStartDate(newStart);
            setEndDate(newEnd);
        }
    }, [year, month, customFilter]);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const data = await getBranchExpenses(branchId, startDate, endDate, { page, pageSize });
            const normalized = normalizePaginatedResponse(data);
            setExpenses(normalized.data || []);
            setTotalExpenses(normalized.total || 0);
            setTotalPages(
                normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / pageSize))
            );
        } catch (error) {
            console.error("Error fetching expenses:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const totalAmount = expenses.reduce((sum, exp) => sum + exp.amount, 0);
        setTotal(totalAmount);
    }, [expenses]);

    useEffect(() => {
        fetchExpenses();
    }, [startDate, endDate, branchId, page]);

    useEffect(() => {
        setPage(1);
    }, [startDate, endDate, branchId]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const handleDelete = async (id) => {
        if (!canDeleteExpense) return;
        const approved = await confirm({
            title: 'Eliminar gasto',
            message: '¿Estás seguro de eliminar este gasto?',
            confirmLabel: 'Eliminar',
            cancelLabel: 'Cancelar',
            tone: 'danger',
        });
        if (!approved) return;
        try {
            await deleteBranchExpense(branchId, id);
            fetchExpenses();
        } catch (err) {
            notify("Error al eliminar");
        }
    };

    const handleSave = async (form) => {
        try {
            if (form.id) {
                if (!canUpdateExpense) return;
                await updateBranchExpense(branchId, form.id, form);
            } else {
                if (!canCreateExpense) return;
                await createBranchExpense(branchId, form);
            }
            setShowModal(false);
            setSelectedExpense(null);
            fetchExpenses();
        } catch (err) {
            notify("Error al guardar el gasto");
        }
    };

    return (
        <div className="max-w-3xl mx-auto p-4">
            <div className="mb-4">
                <div className="flex justify-between items-center">
                    <h1 className="text-xl font-bold">Gastos de la Sucursal</h1>
                    {canCreateExpense && (
                        <button
                            onClick={() => {
                                setSelectedExpense(null);
                                setShowModal(true);
                            }}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2"
                        >
                            <FiPlus /> Nuevo Gasto
                        </button>
                    )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-2">
                    <div className="text-lg font-semibold text-gray-800">
                        Total del período:{" "}
                        <span className="text-black text-xl">
                            ${formatPesoAmount(total)}
                        </span>
                    </div>
                    <div className="mt-2 sm:mt-0">
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={customFilter}
                                onChange={(e) => setCustomFilter(e.target.checked)}
                            />
                            Filtrar por rango específico
                        </label>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-between">
                {/* Año y Mes */}
                {!(customFilter && window.innerWidth < 640) && (
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm">Año</label>
                            <select
                                value={year}
                                onChange={(e) => setYear(Number(e.target.value))}
                                className={`w-full border rounded px-2 py-1 ${customFilter ? "bg-gray-200 cursor-not-allowed" : ""}`}
                                disabled={customFilter}
                            >
                                {[...Array(5)].map((_, i) => {
                                    const y = now.getFullYear() - i;
                                    return <option key={y} value={y}>{y}</option>;
                                })}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm">Mes</label>
                            <select
                                value={month}
                                onChange={(e) => setMonth(Number(e.target.value))}
                                className={`w-full border rounded px-2 py-1 ${customFilter ? "bg-gray-200 cursor-not-allowed" : ""}`}
                                disabled={customFilter}
                            >
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i} value={i}>
                                        {new Date(0, i).toLocaleString("es-CL", { month: "long" })}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Fecha específica */}
                {customFilter && (
                    <DateRangePresetPicker
                        label="Rango personalizado"
                        presetLabel="Rango rapido"
                        startLabel="Desde"
                        endLabel="Hasta"
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={setStartDate}
                        onEndDateChange={setEndDate}
                        containerClassName="w-full"
                        labelClassName="block text-sm font-medium mb-1"
                        inputLabelClassName="block text-xs text-gray-600 mb-1"
                        selectClassName="border rounded px-2 py-1 w-full"
                        inputClassName="border rounded px-2 py-1 w-full"
                    />
                )}
            </div>

            {/* Expense List */}
            {loading ? (
                <p>
                    <InlineLoading />
                </p>
            ) : expenses.length === 0 ? (
                <p>No hay gastos registrados.</p>
            ) : (
                <div className="space-y-3">
                    {expenses.map((exp) => (
                        <div key={exp.id} className="bg-white shadow p-4 rounded flex justify-between items-center">
                            <div>
                                <p className="text-lg font-semibold">{exp.description}</p>
                                <p className="text-sm text-gray-500">
                                    ${formatPesoAmount(exp.amount)} - {new Date(exp.date).toLocaleDateString("es-CL")} ({exp.paymentMethod})
                                </p>
                            </div>
                            <div className="flex gap-2">
                                {canUpdateExpense && (
                                    <button
                                        onClick={() => {
                                            setSelectedExpense(exp);
                                            setShowModal(true);
                                        }}
                                        className="text-blue-500 hover:text-blue-700"
                                    >
                                        <FiEdit />
                                    </button>
                                )}
                                {canDeleteExpense && (
                                    <button
                                        onClick={() => handleDelete(exp.id)}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <FiTrash2 />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {totalExpenses > pageSize && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-500 mt-4">
                    <div>
                        Mostrando {totalExpenses === 0 ? 0 : (page - 1) * pageSize + 1}-
                        {Math.min(page * pageSize, totalExpenses)} de {totalExpenses}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                            disabled={page === 1}
                            className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                        >
                            Anterior
                        </button>
                        <span>
                            {page} / {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={page >= totalPages}
                            className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            )}

            {showModal && (
                <ExpenseModal
                    expense={selectedExpense}
                    onClose={() => setShowModal(false)}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default BranchExpensesPage;
